import React, { useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, FolderUp, Loader, Upload } from 'lucide-react';
import { EmptyState } from '../../components/ui/EmptyState';
import { JobType } from '../../graphql/types';
import { setDraftTape } from '../draftHandoff';
import { useDraftJob } from '../useDraftJob';
import * as intakeData from './intakeDDData';
import { formatExtractLabel, parseManualLoans } from './intakeDDModel';
import type { Tape } from './intakeDDModel';

/**
 * DraftIntakeDD: the Intake Mode of a Due Diligence job that has not been
 * created yet (prototype `showDdDraft`).
 *
 * Stage 1 is the empty loan-list card, "Drop a loan tape, or add loans by hand",
 * with "Choose tape file" and "Add loans manually". Stage 2 is locked with
 * "Documents unlock once the loan list exists". There is no create form: the
 * job is created on the first real action, which is choosing a tape or adding
 * loans, with a default name. The Workspace then replaces the URL with the new
 * job's Intake. A chosen tape is handed over parsed, so it opens in the column
 * mapping; manual loans are created on the new job before the hand-over.
 *
 * If creation fails, the entries stay on screen and an inline error offers
 * Retry. Retrying reuses a job that was already created.
 *
 * Requirements: 2.3, 3.5, 4.1.
 */
const OVERLINE = 'text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500';
const CARD = 'rounded-xl border border-gray-200 bg-white p-[18px]';
const PRIMARY_BUTTON =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[9px] bg-[#834DFB] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#6E2EF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#834DFB]';
const SECONDARY_BUTTON =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-[13px] font-bold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white';

const LOCKED_REASON = 'Add loans first. Documents unlock once the loan list exists.';

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The job exists but its loans could not all be added. */
class LoansNotAdded extends Error {}

export const DraftIntakeDD: React.FC = () => {
  const draft = useDraftJob(JobType.DD);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualText, setManualText] = useState('');
  const [dropActive, setDropActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const retry = useRef<(() => Promise<void>) | null>(null);
  // Manual rows already created on the new job, so a retry does not repeat them.
  const createdRows = useRef<Set<number>>(new Set());
  const tapeInputRef = useRef<HTMLInputElement>(null);

  const manual = useMemo(() => parseManualLoans(manualText), [manualText]);

  /** Run one create step. On failure keep the entries and offer Retry. */
  const attempt = async (step: () => Promise<void>) => {
    retry.current = () => attempt(step);
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (err) {
      console.error('[DraftIntakeDD] Draft action failed:', err);
      setError(
        err instanceof LoansNotAdded
          ? 'The loans could not be added. Your entries are kept. Check your connection and try again.'
          : 'The job could not be created. Your entries are kept. Check your connection and try again.'
      );
      setBusy(false);
    }
  };

  const startWithTape = (tape: Tape) =>
    attempt(async () => {
      const response = await draft.create();
      setDraftTape(response.job.jobId, tape);
      draft.open(response);
    });

  const openTape = async (file: File | undefined) => {
    if (!file || busy) return;
    setNotice(null);
    let tape: Tape;
    try {
      // Read the tape first: a file that cannot be read never creates a job.
      tape = await intakeData.parseTapeFile(file);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'That file could not be read.');
      return;
    }
    await startWithTape(tape);
  };

  const addManualLoans = () => {
    const loans = manual.loans;
    if (loans.length === 0 || busy) return;
    return attempt(async () => {
      const response = await draft.create();
      const jobId = response.job.jobId;
      try {
        await intakeData.createLoans(
          jobId,
          loans.filter((l) => !createdRows.current.has(l.rowIndex)),
          ({ rowIndex }) => createdRows.current.add(rowIndex)
        );
      } catch (err) {
        console.error('[DraftIntakeDD] Failed to add loans:', err);
        throw new LoansNotAdded();
      }
      draft.open(response);
    });
  };

  return (
    <div data-testid="intake-dd" className="flex max-w-[1100px] flex-col gap-4">
      <input
        ref={tapeInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        aria-label="Loan tape file"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          void openTape(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      <div
        data-testid="intake-empty"
        onDragOver={(e) => {
          e.preventDefault();
          setDropActive(true);
        }}
        onDragLeave={() => setDropActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDropActive(false);
          void openTape(e.dataTransfer.files[0]);
        }}
        className={`rounded-[14px] ${dropActive ? 'bg-[#F5F3FF]' : ''}`}
      >
        <p className={`${OVERLINE} mb-2`}>Stage 1 · Loan list</p>
        <EmptyState
          icon={<Upload size={20} strokeWidth={2} aria-hidden="true" />}
          title="Drop a loan tape, or add loans by hand"
          description="XLSX or CSV. You'll check the column mapping before anything is extracted. Documents unlock once the loan list exists."
          action={
            <>
              <button
                type="button"
                className={PRIMARY_BUTTON}
                disabled={busy}
                onClick={() => tapeInputRef.current?.click()}
              >
                {busy ? (
                  <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                ) : null}
                Choose tape file
              </button>
              <button
                type="button"
                className={SECONDARY_BUTTON}
                aria-expanded={manualOpen}
                disabled={busy}
                onClick={() => setManualOpen((open) => !open)}
              >
                Add loans manually
              </button>
            </>
          }
        />
      </div>

      {manualOpen && (
        <section aria-label="Add loans manually" className={CARD}>
          <label htmlFor="manual-loans" className="text-sm font-bold text-gray-900">
            One loan per line
          </label>
          <p className="mt-0.5 text-xs text-gray-500">
            Type the loan number, then a comma and the borrower name if you have it. For example: LN-1001, Jane Doe
          </p>
          <textarea
            id="manual-loans"
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            disabled={busy}
            rows={5}
            className="mt-2 w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-[#834DFB] focus:outline-none focus:ring-2 focus:ring-[#834DFB]/30 disabled:opacity-60"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <p data-testid="manual-count" aria-live="polite" className="text-xs text-gray-500">
              {plural(manual.loans.length, 'loan', 'loans')}
              {manual.duplicates > 0 && ` · ${plural(manual.duplicates, 'repeat', 'repeats')} skipped`}
            </p>
            <button
              type="button"
              className={PRIMARY_BUTTON}
              disabled={busy || manual.loans.length === 0}
              onClick={() => void addManualLoans()}
            >
              {busy ? (
                <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
              ) : null}
              Create {plural(manual.loans.length, 'loan', 'loans')}
            </button>
          </div>
        </section>
      )}

      {notice && (
        <p role="status" className="rounded-lg border border-[#fef08a] bg-[#fefce8] p-2.5 text-[13px] text-[#854d0e]">
          {notice}
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-[#fee2e2] px-3 py-2 text-sm text-[#991b1b]"
        >
          <AlertCircle size={16} strokeWidth={2} className="mt-0.5 shrink-0" aria-hidden="true" />
          <p className="flex-1">{error}</p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void retry.current?.()}
            className="shrink-0 rounded font-bold underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#991b1b] disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      )}

      <section aria-label="Documents per loan" className={CARD}>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className={OVERLINE}>Stage 2 · Documents per loan</p>
            <h2 className="mt-0.5 text-base font-bold text-gray-900">
              Documents unlock once the loan list exists
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" className={SECONDARY_BUTTON} disabled title={LOCKED_REASON}>
              <FolderUp size={14} strokeWidth={2} aria-hidden="true" />
              Add folder
            </button>
            <button
              type="button"
              data-testid="extract-action"
              className={PRIMARY_BUTTON}
              disabled
              title={LOCKED_REASON}
            >
              {formatExtractLabel(0)}
              <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DraftIntakeDD;
